import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ImageIcon } from "lucide-react";
import { adminShop, assetUrl, errMsg, money } from "../../lib/shopApi";
import { StatusBadge, SUPPLIER_LABEL, btn } from "./shared";
import { ProductEditor } from "./ProductEditor";
import { PrintfulImportDialog } from "./PrintfulImportDialog";

const NEW_PM = {
  title: "New Printed Mint product", kind: "Mercantile", supplier: "printed_mint", status: "draft",
  variants: [{ label: "One size", price: 0, enabled: true }],
};

export const ShopProductsTab = ({ passcode, printfulConnected }) => {
  const [products, setProducts] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [importOpen, setImportOpen] = useState(false);

  const load = useCallback(() => adminShop.products(passcode).then(setProducts).catch(() => toast.error("Couldn't load products.")), [passcode]);
  useEffect(() => { load(); }, [load]);

  const addManual = async () => {
    try {
      const p = await adminShop.createProduct(NEW_PM, passcode);
      await load();
      setOpenId(p.id);
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-6">
        <button type="button" className={btn.primary} onClick={() => setImportOpen(true)} disabled={!printfulConnected} data-testid="admin-shop-import-printful-btn">
          Import from Printful
        </button>
        <button type="button" className={btn.ghost} onClick={addManual} data-testid="admin-shop-add-pm-btn">
          Add a Printed Mint product
        </button>
      </div>
      {!printfulConnected && (
        <p className="text-sm italic text-[#8D6E63] mb-4">Connect Printful (Settings tab) to import your designed products.</p>
      )}
      {!products.length && <p className="text-sm text-[#6D5A50]" data-testid="admin-shop-no-products">No merchandise yet.</p>}
      <ul className="space-y-3">
        {products.map((p) => (
          <li key={p.id} className="border border-[#E7DFD5] rounded-md bg-[#FDFBF7]" data-testid={`admin-shop-product-${p.slug}`}>
            <button type="button" onClick={() => setOpenId(openId === p.id ? null : p.id)} className="w-full flex items-center gap-4 p-4 text-left" data-testid={`admin-shop-product-toggle-${p.slug}`}>
              <div className="w-14 h-14 rounded bg-white border border-[#E7DFD5] overflow-hidden flex-shrink-0">
                {p.images?.[0] ? <img src={assetUrl(p.images[0])} alt="" className="w-full h-full object-contain" /> : <div className="w-full h-full bg-[#EFE6D8] flex items-center justify-center text-[#B9A58E]"><ImageIcon size={18} /></div>}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-display text-xl text-[#4B3A2F] truncate">{p.title}</p>
                <p className="text-xs text-[#6D5A50]">
                  {SUPPLIER_LABEL[p.supplier]} · {p.variants.filter((v) => v.enabled).length} options
                  {p.variants.length ? ` · ${money(Math.min(...p.variants.map((v) => v.price)))}+` : ""}
                </p>
              </div>
              <StatusBadge status={p.status} testid={`admin-shop-product-status-${p.slug}`} />
            </button>
            {openId === p.id && (
              <ProductEditor product={p} passcode={passcode} onSaved={load} onDeleted={() => { setOpenId(null); load(); }} />
            )}
          </li>
        ))}
      </ul>
      <PrintfulImportDialog open={importOpen} onOpenChange={setImportOpen} passcode={passcode} onImported={load} />
    </div>
  );
};
